import { useState } from "react";
import { ArrowRight } from "lucide-react";

const KPIS = [
  { label: "Modelled SKU gaps", value: "$0.75M", sub: "3 hero items shown", color: "#f59e0b", accent: false },
  { label: "Largest gap", value: "+16.6%", sub: "engine oil vs should-cost", color: "#ec1c8c", accent: true },
  { label: "Category rollup", value: "$1.84M", sub: "full oil, brake, bearing classes", color: "#7c3aed", accent: false },
  { label: "Method", value: "Bottom-up", sub: "commodity-indexed buildup", color: "#d1d5db", accent: false },
];

interface Sku {
  name: string;
  unit: string;
  volume: string;
  volumeCount: number;
  paid: number;
  commodity: string;
  commodityNote: string;
  commodityTag: string;
  linkedLabel: string;
  /** First element is the raw-material line linked to the commodity reference. */
  elements: [string, number][];
}

const SKUS: Sku[] = [
  {
    name: "Engine Oil 5W-30",
    unit: "per litre",
    volume: "486K L",
    volumeCount: 486000,
    paid: 3.85,
    commodity: "Base oil (Group II) at $0.92/L",
    commodityNote: "Source: ICIS / Argus base-oil index · May 2026",
    commodityTag: "crude pass-through, +11% YoY",
    linkedLabel: "Base oil linked",
    elements: [
      ["Base oil (raw material)", 1.79],
      ["Additives & blending", 0.54],
      ["Packaging", 0.24],
      ["Conversion & labor", 0.18],
      ["Logistics", 0.16],
      ["Supplier margin", 0.3],
    ],
  },
  {
    name: "Brake Disc, cast",
    unit: "per unit",
    volume: "36K units",
    volumeCount: 36000,
    paid: 24.5,
    commodity: "Grey cast iron at $612/t",
    commodityNote: "Source: LME / Fastmarkets scrap-iron index · May 2026",
    commodityTag: "scrap pass-through, +4% YoY",
    linkedLabel: "Iron linked",
    elements: [
      ["Cast iron (raw material)", 11.8],
      ["Machining", 3.4],
      ["Coating", 1.3],
      ["Conversion & labor", 2.1],
      ["Logistics", 1.06],
      ["Supplier margin", 1.8],
    ],
  },
  {
    name: "Wheel Bearing Assembly",
    unit: "per unit",
    volume: "52K units",
    volumeCount: 52000,
    paid: 18.2,
    commodity: "Bearing steel (52100) at $1,140/t",
    commodityNote: "Source: Fastmarkets alloy-steel index · May 2026",
    commodityTag: "alloy surcharge, +6% YoY",
    linkedLabel: "Steel linked",
    elements: [
      ["Bearing steel (raw material)", 6.9],
      ["Heat treat & machining", 4.2],
      ["Seals & grease", 1.4],
      ["Assembly", 1.5],
      ["Logistics", 0.8],
      ["Supplier margin", 2.0],
    ],
  },
];

const BRIDGE_SHARES = [
  { label: "Commodity above index", share: 0.21 / 0.64, color: "#8b5cf6" },
  { label: "Sub-scale volume tiers", share: 0.19 / 0.64, color: "#a855f7" },
  { label: "Excess supplier margin", share: 0.16 / 0.64, color: "#ec4899" },
  { label: "Spec premium (branded)", share: 0.08 / 0.64, color: "#c4b5fd" },
];

const PRICE_PAID = [3.4, 3.52, 3.61, 3.7, 3.78, 3.85];
const MARKET_INDEX = PRICE_PAID;

const VARIANCE = [
  { item: "Engine oil 5W-30", paid: "$3.85", should: "$3.21", variance: "+16.6%", supplier: "Castrol", plant: "Plant A", action: "Dual-source Shell / Valvoline", tone: "act" },
  { item: "Brake disc", paid: "$24.50", should: "$21.46", variance: "+12.4%", supplier: "Continental", plant: "Plant B", action: "Benchmark + re-tender", tone: "act" },
  { item: "Wheel bearing", paid: "$18.20", should: "$16.80", variance: "+7.7%", supplier: "SKF", plant: "Plant C", action: "Volume tier renegotiation", tone: "act" },
  { item: "Chassis bracket", paid: "$11.40", should: "$11.10", variance: "+2.7%", supplier: "Magna", plant: "Plant A", action: "At benchmark, hold", tone: "hold" },
  { item: "Cabin filter", paid: "$4.20", should: "$4.55", variance: "−7.7%", supplier: "Mann+Hummel", plant: "Plant C", action: "Below benchmark, hold", tone: "good" },
];

const money = (n: number) => `$${n.toFixed(2)}`;

function PriceChart() {
  const w = 1000;
  const h = 230;
  const padL = 45;
  const padR = 6;
  const padT = 12;
  const padB = 28;
  const min = 3.2;
  const max = 4.0;
  const x = (i: number) => padL + (i * (w - padL - padR)) / (PRICE_PAID.length - 1);
  const y = (v: number) => padT + ((max - v) / (max - min)) * (h - padT - padB);
  const line = (series: number[]) => series.map((v, i) => `${i ? "L" : "M"}${x(i)} ${y(v)}`).join(" ");
  const area = `${line(PRICE_PAID)} L${x(PRICE_PAID.length - 1)} ${h - padB} L${x(0)} ${h - padB} Z`;

  return (
    <svg className="sm-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Price paid versus market index">
      <defs>
        <linearGradient id="sm-chart-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ec4899" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#ec4899" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[4.0, 3.8, 3.6, 3.4, 3.2].map((t) => (
        <g key={t}>
          <line x1={0} x2={w} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeDasharray="4 4" />
          <text x={0} y={y(t) - 5} textAnchor="start" fontSize="12" fill="#9ca3af">{`$${t.toFixed(2)}`}</text>
        </g>
      ))}
      <path d={area} fill="url(#sm-chart-fill)" />
      <path d={line(MARKET_INDEX)} fill="none" stroke="#1e1b4b" strokeWidth="2" strokeDasharray="0" />
      <path d={line(PRICE_PAID)} fill="none" stroke="#ec4899" strokeWidth="2" />
      {PRICE_PAID.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="5" fill="#1e1b4b" stroke="#ec4899" strokeWidth="1.5" />
      ))}
      {PRICE_PAID.map((_, i) => (
        <text key={i} x={x(i)} y={h - 6} textAnchor="middle" fontSize="12" fill="#9ca3af">{`Q${i + 1}`}</text>
      ))}
    </svg>
  );
}

export function ShouldCostPanel() {
  const [skuIdx, setSkuIdx] = useState(0);
  const [scenario, setScenario] = useState(0);

  const sku = SKUS[skuIdx];
  const elements = sku.elements.map(([label, v], i) => [label, i === 0 ? v * (1 + scenario / 100) : v] as [string, number]);
  const should = elements.reduce((s, [, v]) => s + v, 0);
  const gap = sku.paid - should;
  const gapPct = (gap / should) * 100;
  const maxEl = Math.max(...elements.map(([, v]) => v));
  const gapAnnual = gap * sku.volumeCount;
  const annualLabel = `$${Math.round(gapAnnual / 1000)}K`;
  const unitShort = sku.unit === "per litre" ? "/L" : "/unit";

  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {KPIS.map(({ label, value, sub, color, accent }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
            <p className="sm-kpi__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title">Should-Cost Model</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">
              Bottom-up cost buildup from a sourced commodity reference, bridged to the <span className="op-accent">price actually paid</span>
            </p>
          </div>
          <span className="sm-tag">Interactive</span>
        </div>

        <div className="si-tabs sm-sku-tabs">
          {SKUS.map((s, i) => (
            <button
              key={s.name}
              type="button"
              onClick={() => setSkuIdx(i)}
              className={`si-tab sm-tab${i === skuIdx ? " si-tab--active" : ""}`}
            >
              {s.name}
            </button>
          ))}
        </div>

        <div className="sm-commodity">
          <span className="sm-commodity__label">Commodity reference</span>
          <strong className="sm-commodity__name">{sku.commodity}</strong>
          <span className="sm-commodity__note">{sku.commodityNote}</span>
          <span className="sm-commodity__tag">{sku.commodityTag}</span>
        </div>

        <div className="sm-sc-grid">
          <div>
            <p className="sm-sc-intro">
              Cost buildup for <strong>{sku.name} ({sku.unit})</strong>. The raw-material element is linked to the commodity
              reference above.
            </p>
            <div className="sm-sc-bars">
              {elements.map(([label, v], i) => (
                <div key={label} className="sm-sc-bar">
                  <div className="sm-sc-bar__head">
                    <span>
                      {label}
                      {i === 0 && <span className="sm-sc-bar__link"> {sku.linkedLabel}</span>}
                    </span>
                    <strong>{money(v)}</strong>
                  </div>
                  <div className="sm-geo__track">
                    <div className="sm-geo__fill" style={{ width: `${(v / maxEl) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="sm-sc-totals">
              <div>
                <p className="sm-sc-totals__label">Modelled should-cost</p>
                <p className="sm-sc-totals__value">
                  {money(should)}
                  <span>{unitShort}</span>
                </p>
              </div>
              <div className="sm-sc-totals__right">
                <p className="sm-sc-totals__label">Price paid</p>
                <p className="sm-sc-totals__value sm-sc-totals__value--accent">
                  {money(sku.paid)}
                  <span>{unitShort}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="sm-sc-side">
            <div className="sm-overpay">
              <p className="sm-overpay__eyebrow">Overpayment vs should-cost</p>
              <p className="sm-overpay__value">
                {gapPct >= 0 ? "+" : ""}
                {gapPct.toFixed(1)}%
              </p>
              <p className="sm-overpay__sub">
                {money(gap)}
                {unitShort} · {annualLabel} on {sku.volume} annual volume
              </p>
            </div>
            <div className="sm-scenario">
              <p className="sm-scenario__eyebrow">Model commodity scenario</p>
              <div className="sm-scenario__range">
                <span>−5%</span>
                <span>+5%</span>
              </div>
              <p className="sm-scenario__value">
                {scenario > 0 ? "+" : scenario < 0 ? "−" : "+"}
                {Math.abs(scenario)}%
              </p>
              <p className="sm-scenario__help">
                Shift the commodity reference to test scenarios. Should-cost, the gap, and the bridge all recompute.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="si-card sm-card">
        <p className="sm-card__title sm-card__title--tight">Why you pay more than should-cost</p>
        <p className="sm-card__subtitle sm-card__subtitle--muted">
          The gap is not all overpayment. The bridge separates what is <span className="sm-green">recoverable (volume, margin)</span>{" "}
          from what is <span className="op-purple">structural (commodity, spec)</span>.
        </p>
        <div className="sm-bridge">
          <div className="sm-bridge__row">
            <p className="sm-bridge__label sm-bridge__label--strong">Price paid</p>
            <div className="sm-bridge__track">
              <div className="sm-bridge__bar" style={{ width: "100%", background: "#a3a8b5" }} />
            </div>
            <p className="sm-bridge__value sm-bridge__value--strong">{money(sku.paid)}</p>
          </div>
          {BRIDGE_SHARES.map(({ label, share, color }) => {
            const delta = Math.max(gap, 0) * share;
            return (
              <div key={label} className="sm-bridge__row">
                <p className="sm-bridge__label">{label}</p>
                <div className="sm-bridge__track">
                  <div className="sm-bridge__bar" style={{ width: `${Math.min((delta / sku.paid) * 400, 100)}%`, background: color }} />
                </div>
                <p className="sm-bridge__value sm-bridge__value--accent">−{money(delta)}</p>
              </div>
            );
          })}
          <div className="sm-bridge__row">
            <p className="sm-bridge__label sm-bridge__label--strong">Should-cost</p>
            <div className="sm-bridge__track">
              <div className="sm-bridge__bar" style={{ width: `${(should / sku.paid) * 100}%`, background: "#312e5a" }} />
            </div>
            <p className="sm-bridge__value sm-bridge__value--strong">{money(should)}</p>
          </div>
        </div>
        <div className="sm-bridge__legend">
          <span><i style={{ background: "#10b981" }} />Recoverable: volume + margin</span>
          <span><i style={{ background: "#7c3aed" }} />Structural: commodity + spec</span>
        </div>
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__pad">
          <p className="sm-card__title sm-card__title--tight">Price paid vs market index, last 6 quarters</p>
          <p className="sm-card__subtitle sm-card__subtitle--link">
            The negotiation trigger: when the market index softens but your price paid keeps climbing, the divergence is
            recoverable. Tracking ICIS base-oil index.
          </p>
          <PriceChart />
          <div className="sm-chart__legend">
            <span><i style={{ background: "#ec4899" }} />Price paid</span>
            <span><i style={{ background: "#1e1b4b" }} />ICIS base-oil index</span>
          </div>
          <p className="sm-chart__note">
            Latest divergence: <strong>+6 pts</strong> price above index, the recoverable signal.
          </p>
        </div>
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Should-Cost &amp; Price</p>
            <p className="sm-callout__text">
              <strong>Engine Oil 5W-30 is paid at $3.85 versus a $3.21 should-cost (+16.6%).</strong> Volume and margin causes
              are directly negotiable; commodity and spec set the floor. Recoverable value is worth $311K at current volume.
            </p>
          </div>
          <button type="button" className="sm-callout__btn">
            Send to Opportunity
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__head sm-card__head--stacked">
          <div>
            <p className="sm-card__title">Unit Price Variance vs Benchmark</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">Same item, cross-plant and cross-supplier spread</p>
          </div>
          <span className="sm-tag">From PO + Invoice</span>
        </div>
        <table className="sm-table">
          <thead>
            <tr>
              <th>Line Item</th>
              <th className="sm-table__right">Paid</th>
              <th className="sm-table__right">Should-Cost</th>
              <th className="sm-table__right">Variance</th>
              <th>Supplier</th>
              <th>Plant</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {VARIANCE.map(({ item, paid, should: sc, variance, supplier, plant, action, tone }) => (
              <tr key={item}>
                <td className="sm-table__strong">{item}</td>
                <td className="sm-table__right">{paid}</td>
                <td className="sm-table__right sm-table__muted">{sc}</td>
                <td className={`sm-table__right sm-table__strong ${tone === "good" ? "sm-green" : "sm-table__accent"}`}>{variance}</td>
                <td>{supplier}</td>
                <td className="sm-table__muted">{plant}</td>
                <td className={tone === "act" ? "op-purple" : tone === "good" ? "sm-green" : "sm-table__muted"}>{action}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="sm-table-note">
          Should-cost column is the bottom-up model output. Negative variance (cabin filter) means the part is already at or
          below should-cost, hold.
        </p>
      </div>
    </div>
  );
}
